import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
    FaArrowRight,
    FaCheckCircle,
    FaEye,
    FaEyeSlash,
    FaFacebookF,
    FaGoogle,
    FaLock,
    FaMapMarkerAlt,
    FaMotorcycle,
    FaShieldAlt,
    FaUser,
    FaWallet,
    FaBolt,
    FaHeadset,
    FaRoute,
} from "react-icons/fa";

import { useSelector, useDispatch } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import toast from "react-hot-toast";

import {
    rider_login,
    messageClear,
} from "../store/reducers/authReducer";
import Headers from "../components/Headers";
import Footer from "../components/Footer";


const Login = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();


    // =========================================================
    // AUTH STATE
    // =========================================================

    const {
        loader,
        successMessage,
        errorMessage,
        userInfo,
    } = useSelector((state) => state.auth);


    // =========================================================
    // REDIRECT STATE
    // =========================================================

    const from = location.state?.from;
    const checkoutState = location.state?.checkoutState;


    // =========================================================
    // FORM STATE
    // =========================================================

    const [state, setState] = useState({
        identifier: "",
        password: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);


    // =========================================================
    // INPUT HANDLE
    // =========================================================

    const inputHandle = (e) => {

        const { name, value } = e.target;

        setState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };


    // =========================================================
    // LOGIN
    // =========================================================

    const login = (e) => {

        e.preventDefault();

        const identifier = state.identifier.trim();
        const password = state.password.trim();


        if (!identifier) {

            toast.error(
                "Please enter your email or mobile number"
            );

            return;
        }


        const isEmail =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);


        const isMobile =
            /^01[3-9]\d{8}$/.test(identifier);


        if (!isEmail && !isMobile) {

            toast.error(
                "Please enter a valid email or mobile number"
            );

            return;
        }


        if (!password) {

            toast.error(
                "Please enter your password"
            );

            return;
        }


        dispatch(
            rider_login({
                identifier,
                password,
            })
        );
    };


    // =========================================================
    // SUCCESS / ERROR MESSAGE
    // =========================================================

    useEffect(() => {

        if (successMessage) {

            toast.success(successMessage);

            dispatch(messageClear());
        }


        if (errorMessage) {

            toast.error(errorMessage);

            dispatch(messageClear());
        }

    }, [
        successMessage,
        errorMessage,
        dispatch,
    ]);




    // =========================================================
    // UI
    // =========================================================

    return (

        <div className="min-h-screen bg-[#fff8f8] text-gray-900">

<Headers/>


            {/* =====================================================
                MAIN
            ===================================================== */}

            <main className="relative overflow-hidden">


                {/* BACKGROUND DECORATION */}

                <div className="pointer-events-none absolute inset-0 overflow-hidden">

                    <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-red-100/60 blur-[100px]" />

                    <div className="absolute -right-32 top-72 h-96 w-96 rounded-full bg-red-50 blur-[100px]" />

                    <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-orange-50/50 blur-[100px]" />

                </div>


                {/* CONTENT */}

                <div className="relative mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-10 lg:px-8 lg:py-14">


                    {/* MAIN CARD */}

                    <div className="overflow-hidden rounded-[30px] border border-gray-100 bg-white shadow-[0_25px_90px_rgba(15,23,42,0.08)]">


                        <div className="grid lg:grid-cols-2">


                            {/* =================================================
                                LEFT SIDE
                            ================================================= */}

                            <section className="relative min-h-[650px] overflow-hidden bg-[#161616] px-7 py-9 text-white sm:px-10 sm:py-12 lg:px-12 xl:px-16">


                                {/* RED GLOW */}

                                <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-red-600/25 blur-[100px]" />

                                <div className="absolute -bottom-40 -right-32 h-[450px] w-[450px] rounded-full bg-red-600/15 blur-[110px]" />


                                {/* GRID */}

                                <div
                                    className="pointer-events-none absolute inset-0 opacity-[0.035]"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                                        backgroundSize: "44px 44px",
                                    }}
                                />


                                <div className="relative z-10 flex h-full flex-col">


                                    {/* BADGE */}

                                    <div className="flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-2 backdrop-blur-md">

                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600">

                                            <FaBolt size={8} />

                                        </span>


                                        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-white/75">

                                            FoodBite24 Rider

                                        </span>

                                    </div>


                                    {/* TITLE */}

                                    <div className="mt-9 max-w-lg">

                                        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-red-400">

                                            Deliver • Earn • Grow

                                        </p>


                                        <h1 className="mt-4 text-[40px] font-black leading-[1.05] tracking-[-0.04em] sm:text-5xl xl:text-[57px]">

                                            Your ride.
                                            <br />

                                            <span className="text-red-500">
                                                Your journey.
                                            </span>

                                        </h1>


                                        <p className="mt-5 max-w-md text-sm font-medium leading-7 text-white/50">

                                            Everything you need to manage
                                            deliveries, routes and your rider
                                            journey — designed in one simple
                                            platform.

                                        </p>

                                    </div>


                                    {/* =================================================
                                        CENTER RIDER VISUAL
                                    ================================================= */}

                                    <div className="relative mt-10 hidden flex-1 items-center justify-center md:flex">


                                        {/* ROUTE LINE */}

                                        <div className="absolute left-[8%] right-[8%] top-1/2 h-px border-t border-dashed border-white/10" />


                                        {/* LOCATION */}

                                        <div className="absolute left-0 top-5 rounded-2xl border border-white/10 bg-white/[0.07] p-3 backdrop-blur-xl">

                                            <div className="flex items-center gap-3">

                                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-500/15 text-red-400">

                                                    <FaMapMarkerAlt size={13} />

                                                </div>


                                                <div>

                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/35">
                                                        Delivery
                                                    </p>

                                                    <p className="mt-0.5 text-xs font-black">
                                                        Live Route
                                                    </p>

                                                </div>

                                            </div>

                                        </div>


                                        {/* WALLET */}

                                        <div className="absolute right-0 top-20 rounded-2xl border border-white/10 bg-white/[0.07] p-3 backdrop-blur-xl">

                                            <div className="flex items-center gap-3">

                                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-500/10 text-green-400">

                                                    <FaWallet size={13} />

                                                </div>


                                                <div>

                                                    <p className="text-[8px] font-bold uppercase tracking-wider text-white/35">
                                                        Earnings
                                                    </p>

                                                    <p className="mt-0.5 text-xs font-black">
                                                        Track Easily
                                                    </p>

                                                </div>

                                            </div>

                                        </div>


                                        {/* BIKE */}

                                        <div className="relative flex h-48 w-48 items-center justify-center rounded-full border border-white/[0.06] bg-white/[0.025]">

                                            <div className="absolute inset-5 rounded-full border border-red-500/20" />

                                            <div className="absolute inset-12 rounded-full border border-red-500/10" />

                                            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-red-600 text-white shadow-[0_20px_60px_rgba(220,38,38,0.35)]">

                                                <FaMotorcycle size={50} />

                                            </div>

                                        </div>

                                    </div>


                                    {/* FEATURES */}

                                    <div className="mt-10 grid grid-cols-3 gap-3 border-t border-white/10 pt-6">


                                        <div>

                                            <FaRoute
                                                size={14}
                                                className="mb-2 text-red-500"
                                            />

                                            <p className="text-[11px] font-black">
                                                Smart Routes
                                            </p>

                                            <p className="mt-1 text-[8px] text-white/35">
                                                Easy navigation
                                            </p>

                                        </div>


                                        <div>

                                            <FaWallet
                                                size={14}
                                                className="mb-2 text-red-500"
                                            />

                                            <p className="text-[11px] font-black">
                                                Earnings
                                            </p>

                                            <p className="mt-1 text-[8px] text-white/35">
                                                Track income
                                            </p>

                                        </div>


                                        <div>

                                            <FaHeadset
                                                size={14}
                                                className="mb-2 text-red-500"
                                            />

                                            <p className="text-[11px] font-black">
                                                Support
                                            </p>

                                            <p className="mt-1 text-[8px] text-white/35">
                                                Rider help
                                            </p>

                                        </div>

                                    </div>

                                </div>

                            </section>


                            {/* =================================================
                                RIGHT SIDE
                            ================================================= */}

                            <section className="flex items-center justify-center bg-white px-6 py-10 sm:px-10 lg:px-12 xl:px-16">


                                <div className="w-full max-w-[420px]">


                                    {/* MOBILE LOGO */}

                                    <div className="mb-8 flex items-center gap-3 lg:hidden">

                                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg">

                                            <FaMotorcycle size={19} />

                                        </div>


                                        <div>

                                            <p className="text-lg font-black leading-none">

                                                FoodBite
                                                <span className="text-red-600">
                                                    24
                                                </span>

                                            </p>


                                            <p className="mt-1 text-[8px] font-black uppercase tracking-[0.2em] text-gray-400">

                                                Rider Partner

                                            </p>

                                        </div>

                                    </div>


                                    {/* FORM TITLE */}

                                    <div className="mb-8">

                                        <div className="mb-4 flex items-center gap-2">

                                            <span className="h-1 w-8 rounded-full bg-red-600" />

                                            <span className="text-[9px] font-black uppercase tracking-[0.22em] text-red-600">

                                                Rider Access

                                            </span>

                                        </div>


                                        <h2 className="text-[32px] font-black leading-[1.08] tracking-[-0.03em] text-gray-950 sm:text-[37px]">

                                            Welcome back,

                                            <br />

                                            <span className="text-red-600">
                                                Rider.
                                            </span>

                                        </h2>


                                        <p className="mt-3 max-w-sm text-sm leading-6 text-gray-500">

                                            Sign in to your FoodBite24 rider
                                            account and continue your journey.

                                        </p>

                                    </div>


                                    {/* FORM */}

                                    <form
                                        onSubmit={login}
                                        className="space-y-5"
                                    >


                                        {/* EMAIL / MOBILE */}

                                        <div>

                                            <label
                                                htmlFor="identifier"
                                                className="mb-2 block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                            >

                                                Email or Mobile

                                            </label>


                                            <div className="group relative">

                                                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">

                                                    <FaUser size={14} />

                                                </div>


                                                <input
                                                    id="identifier"
                                                    name="identifier"
                                                    type="text"
                                                    value={state.identifier}
                                                    onChange={inputHandle}
                                                    placeholder="Email or mobile number"
                                                    autoComplete="username"
                                                    className="h-[55px] w-full rounded-2xl border border-gray-200 bg-gray-50 pl-11 pr-4 text-sm font-semibold text-gray-900 outline-none transition-all placeholder:text-gray-400 hover:border-gray-300 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/10"
                                                />

                                            </div>

                                        </div>


                                        {/* PASSWORD */}

                                        <div>

                                            <div className="mb-2 flex items-center justify-between">

                                                <label
                                                    htmlFor="password"
                                                    className="block text-[10px] font-black uppercase tracking-[0.08em] text-gray-600"
                                                >

                                                    Password

                                                </label>


                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toast(
                                                            "Password recovery is coming soon."
                                                        )
                                                    }
                                                    className="text-[10px] font-black text-red-600 hover:text-red-700"
                                                >

                                                    Forgot Password?

                                                </button>

                                            </div>


                                            <div className="group relative">

                                                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 transition group-focus-within:text-red-600">

                                                    <FaLock size={14} />

                                                </div>


                                                <input
                                                    id="password"
                                                    name="password"
                                                    type={
                                                        showPassword
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    value={state.password}
                                                    onChange={inputHandle}
                                                    placeholder="Enter your password"
                                                    autoComplete="current-password"
                                                    className="h-[55px] w-full rounded-2xl border border-gray-200 bg-gray-50 pl-11 pr-12 text-sm font-semibold text-gray-900 outline-none transition-all placeholder:text-gray-400 hover:border-gray-300 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/10"
                                                />


                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (prev) => !prev
                                                        )
                                                    }
                                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-red-600"
                                                    aria-label={
                                                        showPassword
                                                            ? "Hide password"
                                                            : "Show password"
                                                    }
                                                >

                                                    {showPassword ? (
                                                        <FaEyeSlash size={16} />
                                                    ) : (
                                                        <FaEye size={16} />
                                                    )}

                                                </button>

                                            </div>

                                        </div>


                                        {/* REMEMBER */}

                                        <div className="flex items-center justify-between">

                                            <label className="flex cursor-pointer items-center gap-2">

                                                <input
                                                    type="checkbox"
                                                    checked={rememberMe}
                                                    onChange={(e) =>
                                                        setRememberMe(
                                                            e.target.checked
                                                        )
                                                    }
                                                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-red-600"
                                                />


                                                <span className="text-xs font-semibold text-gray-500">

                                                    Remember me

                                                </span>

                                            </label>


                                            <div className="flex items-center gap-1.5 text-[9px] font-bold text-gray-400">

                                                <FaShieldAlt
                                                    size={10}
                                                    className="text-green-500"
                                                />

                                                Secure login

                                            </div>

                                        </div>


                                        {/* LOGIN */}

                                        <button
                                            type="submit"
                                            
                                            disabled={loader}
                                            className="group relative flex h-[56px] w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-red-600 text-sm font-black text-white shadow-[0_14px_35px_rgba(220,38,38,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-[0_18px_40px_rgba(220,38,38,0.28)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                                        >

                                            <span className="pointer-events-none absolute inset-y-0 -left-20 w-16 rotate-12 bg-white/20 blur-lg transition-all duration-700 group-hover:left-[110%]" />


                                            {loader ? (

                                                <FadeLoader
                                                    height={7}
                                                    width={2}
                                                    radius={2}
                                                    margin={2}
                                                    color="#ffffff"
                                                />

                                            ) : (

                                                <>

                                                    <span>
                                                        Sign In to Rider Account
                                                    </span>

                                                    <FaArrowRight
                                                        size={12}
                                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                                    />

                                                </>

                                            )}

                                        </button>

                                    </form>


                                    {/* SECURITY */}

                                    <div className="mt-5 flex items-center gap-3 rounded-2xl border border-green-100 bg-green-50/70 px-4 py-3">

                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-green-600 shadow-sm">

                                            <FaShieldAlt size={13} />

                                        </div>


                                        <div>

                                            <p className="text-[10px] font-black text-green-700">

                                                Secure Rider Login

                                            </p>


                                            <p className="mt-0.5 text-[9px] font-medium text-green-600/70">

                                                Your account information is protected.

                                            </p>

                                        </div>

                                    </div>


                                    {/* DIVIDER */}

                                    <div className="my-6 flex items-center gap-3">

                                        <div className="h-px flex-1 bg-gray-200" />

                                        <span className="text-[9px] font-black tracking-[0.15em] text-gray-400">
                                            OR
                                        </span>

                                        <div className="h-px flex-1 bg-gray-200" />

                                    </div>


                                    {/* SOCIAL LOGIN */}

                                    <div className="grid grid-cols-2 gap-3">

                                        <button
                                            type="button"
                                            onClick={() =>
                                                toast(
                                                    "Facebook login is coming soon."
                                                )
                                            }
                                            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-xs font-black text-gray-700 transition hover:bg-gray-50 active:scale-[0.98]"
                                        >

                                            <FaFacebookF
                                                size={13}
                                                className="text-[#1877F2]"
                                            />

                                            Facebook

                                        </button>


                                        <button
                                            type="button"
                                            onClick={() =>
                                                toast(
                                                    "Google login is coming soon."
                                                )
                                            }
                                            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-xs font-black text-gray-700 transition hover:bg-gray-50 active:scale-[0.98]"
                                        >

                                            <FaGoogle
                                                size={13}
                                                className="text-red-500"
                                            />

                                            Google

                                        </button>

                                    </div>


                                    {/* REGISTER */}

                                    <div className="mt-7 text-center">

                                        <p className="text-xs font-medium text-gray-500">

                                            New to FoodBite24 Rider?

                                        </p>


                                        <Link
                                            to="/register"
                                            state={{
                                                from,
                                                checkoutState,
                                            }}
                                            className="mt-2 inline-flex items-center gap-2 text-sm font-black text-red-600 transition hover:text-red-700"
                                        >

                                            Create Rider Account

                                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-50">

                                                <FaArrowRight size={9} />

                                            </span>

                                        </Link>

                                    </div>


                                    {/* TRUST */}

                                    <div className="mt-6 flex items-center justify-center gap-2 text-[9px] font-semibold text-gray-400">

                                        <FaCheckCircle
                                            size={10}
                                            className="text-green-500"
                                        />

                                        Trusted FoodBite24 Rider Portal

                                    </div>

                                </div>

                            </section>

                        </div>

                    </div>

                </div>

            </main>




            {/* =====================================================
                LOADING OVERLAY
            ===================================================== */}

            {loader && (

                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 p-4 backdrop-blur-md">

                    <div className="w-full max-w-[270px] rounded-[26px] border border-white/70 bg-white p-8 text-center shadow-[0_30px_100px_rgba(0,0,0,0.20)]">


                        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">

                            <FaMotorcycle size={24} />

                        </div>


                        <div className="flex justify-center">

                            <FadeLoader
                                height={7}
                                width={3}
                                radius={2}
                                margin={2}
                                color="#dc2626"
                            />

                        </div>


                        <p className="mt-5 text-sm font-black text-gray-900">

                            Signing you in...

                        </p>


                        <p className="mt-1 text-[10px] font-medium text-gray-400">

                            Preparing your rider account

                        </p>

                    </div>

                </div>

            )}
    <Footer/>
        </div>
    );
};


export default Login;
